import { Component, ElementRef, Input, OnInit } from '@angular/core';
import {
  HANDOVER_STATUS_ACCEPTED,
  HANDOVER_TYPE_HO3_ID,
  MANAGEMENT_UNIT_GSJ,
  WELL_TYPE_NAME_INJECTOR,
} from 'src/app/handover-constants';
import {
  AnnulusFluidBase,
  AnnulusSpace,
} from 'src/app/interfaces/annulus-space';
import { Handover } from 'src/app/interfaces/handover';
import { WellType } from 'src/app/interfaces/notifications';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SectionTouched } from '../shared/section-touched.class';

@Component({
  selector: 'app-handover-detail-annulus-spaces',
  templateUrl: './handover-detail-annulus-spaces.component.html',
  styleUrls: ['./handover-detail-annulus-spaces.component.scss'],
  standalone: false,
})
export class HandoverDetailAnnulusSpacesComponent
  extends SectionTouched
  implements OnInit
{
  @Input() handover: Handover | undefined;
  @Input() readOnlyContent!: boolean;
  @Input() isEngineerUser!: boolean;

  annulusSpaces: AnnulusSpace[] = [];
  annulusFluidBases: AnnulusFluidBase[] = [];
  displayedColumns = [
    'numAnular',
    'diamTubInt',
    'diamTubExt',
    'annulusFluidBaseId',
    'averageDensity',
    'actions',
  ];
  isLoading = false;
  originalAnnulusSpaces: AnnulusSpace[] = [];
  isDisabled: boolean[] = [];
  hideFooter = true;
  showFooterNoDataOrign = false;
  isHO3 = false;
  showInjectorWells = false;
  injectorWellsHasPendingChanges = false;

  constructor(
    public handoverService: HandoverService,
    public dialogService: DialogService,
    public notificationService: NotificationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getAnnulusFluidBases();
    if (this.handover != undefined) {
      this.setAnnulusSpacesDataSource();
      this.resolveInjectorWellsVisibility();
      this.showFooterNoDataOrign =
        this.handover.handoverStatusId != HANDOVER_STATUS_ACCEPTED;
      this.isHO3 = this.handover.handoverTypeId == HANDOVER_TYPE_HO3_ID;
      this.readOnlyContent =
        this.readOnlyContent || (this.isHO3 && !this.isEngineerUser);
      if (this.handover.handoverTypeId != HANDOVER_TYPE_HO3_ID) {
        // Si no es HO3, agrega columna hasMeasuredPressure para mostrar.
        this.displayedColumns.splice(3, 0, 'hasMeasuredPressure');
        this.displayedColumns.splice(4, 0, 'pressureMeasureDate');
        this.displayedColumns.splice(5, 0, 'measuredPressure');
      } else {
        this.displayedColumns.splice(3, 0, 'fechaFin');
        this.displayedColumns.splice(4, 0, 'presionCasing');
      }
    }
  }

  /**
   * La subsección "Pozos Inyectores" se muestra sólo si el pozo es de la UG GSJ
   * o si el tipo de pozo elegido en Notificaciones es "Inyector".
   */
  resolveInjectorWellsVisibility(): void {
    if (this.handover?.wellManagementUnit == MANAGEMENT_UNIT_GSJ) {
      this.showInjectorWells = true;
      return;
    }
    if (this.handover?.handoverId == undefined) {
      return;
    }
    this.handoverService
      .getWellTypes(this.handover.handoverId)
      .subscribe((wellTypes: WellType[]) => {
        this.showInjectorWells =
          wellTypes?.some(
            (wellType) =>
              true === wellType.chosen &&
              wellType.name == WELL_TYPE_NAME_INJECTOR
          ) ?? false;
      });
  }

  getAnnulusFluidBases(): void {
    this.handoverService
      .getAnnulusFluidBases()
      .subscribe((bases: AnnulusFluidBase[]) => {
        this.annulusFluidBases = bases;
        const nothing: AnnulusFluidBase = {
          annulusFluidBaseId: null!,
          annulusFluidBaseName: '',
        };
        this.annulusFluidBases.push(nothing);
      });
  }

  setAnnulusSpacesDataSource(): void {
    if (this.handover != undefined) {
      this.isLoading = true;
      this.handoverService
        .getAnnulusSpaces(
          this.handover.handoverId!,
          this.handover.wellId!,
          this.handover.handoverStatusId!,
          this.handover.handoverTypeId!
        )
        .subscribe((spaces: AnnulusSpace[]) => {
          this.annulusSpaces = spaces;
          this.setOriginalAnnulusSpacesValues(spaces);
          this.isLoading = false;
          this.hideFooter = spaces.length > 0;
        });
    }
  }

  setOriginalAnnulusSpacesValues(spaces: AnnulusSpace[]): void {
    this.originalAnnulusSpaces = spaces.map((space) => {
      this.isDisabled.push(true);

      if (space.comments == null) space.comments = '';
      return Object.assign({ isDisabled: true }, space);
    });
  }

  enableSaveButton(i: number): void {
    const oSp = this.originalAnnulusSpaces[i];
    const enable =
      this.annulusSpaces[i].hasMeasuredPressure != oSp.hasMeasuredPressure ||
      this.annulusSpaces[i].pressureMeasureDate != oSp.pressureMeasureDate ||
      this.annulusSpaces[i].measuredPressure != oSp.measuredPressure ||
      this.annulusSpaces[i].annulusFluidBaseId != oSp.annulusFluidBaseId ||
      this.annulusSpaces[i].averageDensity != oSp.averageDensity ||
      this.annulusSpaces[i].comments != oSp.comments;
    this.isDisabled[i] = !enable;
    this.reportPendingChanges();
  }

  openComments(
    element: AnnulusSpace,
    index: number,
    event: any,
    readOnly: boolean
  ): void {
    const target = new ElementRef(event.currentTarget);
    this.dialogService
      .openCommentDialog(
        'Indique Base Fluido Anular en caso de haber seleccionado "Other"',
        this.annulusSpaces[index].comments,
        target,
        -200,
        -450,
        readOnly
      )
      .afterClosed()
      .subscribe((comments: string) => {
        this.annulusSpaces[index].comments = comments;
        this.enableSaveButton(index);
      });
  }

  save(element: AnnulusSpace, index: number): void {
    const dialogRef = this.dialogService.openConfirmDialog(
      'Espacio Anular',
      `¿Desea guardar los cambios realizados en el Espacio Anular ${element.numAnular}?`
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        element.handoverId = this.handover?.handoverId;
        if (element.averageDensity?.toString() == '') {
          element.averageDensity = null!;
        }
        const spaces: AnnulusSpace[] = [];
        spaces.push(element);

        this.handoverService
          .saveAnnulusSpaces(spaces)
          .subscribe((savedSpaces: AnnulusSpace[]) => {
            if (savedSpaces) {
              this.notificationService.notify(
                `Los cambios en el Espacio Anular ${element.numAnular} se han guardado con éxito.`,
                true
              );
              this.isDisabled[index] = true;
              this.originalAnnulusSpaces[index].hasMeasuredPressure =
                element.hasMeasuredPressure;
              this.originalAnnulusSpaces[index].pressureMeasureDate =
                element.pressureMeasureDate;
              this.originalAnnulusSpaces[index].measuredPressure =
                element.measuredPressure;
              this.originalAnnulusSpaces[index].annulusFluidBaseId =
                element.annulusFluidBaseId;
              this.originalAnnulusSpaces[index].averageDensity =
                element.averageDensity;
              this.originalAnnulusSpaces[index].comments = element.comments;
              this.reportPendingChanges();
            } else {
              this.notificationService.notify(
                `Ocurrió un error al grabar los cambios en el Espacio Anular ${element.numAnular}.`,
                false
              );
            }
          });
      }
    });
  }

  cleanDataAssociated(indexAnnulusElement: number): void {
    this.annulusSpaces[indexAnnulusElement].pressureMeasureDate = undefined;
    this.annulusSpaces[indexAnnulusElement].measuredPressure = undefined;
  }

  onInjectorWellsPendingChanges(pendingChanges: boolean): void {
    this.injectorWellsHasPendingChanges = pendingChanges;
    this.reportPendingChanges();
  }

  reportPendingChanges(): void {
    let thereAreChanges = this.injectorWellsHasPendingChanges;
    for (let index = 0; index < this.annulusSpaces.length; index++) {
      thereAreChanges =
        thereAreChanges ||
        this.annulusSpaces[index].hasMeasuredPressure !=
          this.originalAnnulusSpaces[index].hasMeasuredPressure ||
        this.annulusSpaces[index].pressureMeasureDate !=
          this.originalAnnulusSpaces[index].pressureMeasureDate ||
        this.annulusSpaces[index].measuredPressure !=
          this.originalAnnulusSpaces[index].measuredPressure ||
        this.annulusSpaces[index].annulusFluidBaseId !=
          this.originalAnnulusSpaces[index].annulusFluidBaseId ||
        this.annulusSpaces[index].averageDensity !=
          this.originalAnnulusSpaces[index].averageDensity ||
        this.annulusSpaces[index].comments !=
          this.originalAnnulusSpaces[index].comments;
    }
    this.emitPendingChanges(thereAreChanges);
  }
}
